import React, { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { User, UserType } from "../../../types";
import { useBlockUser, useUnBlockUser } from "../../../store";

interface BlockUserModalProps {
  open: boolean;
  user: User;
  onClose: () => void;
}

const BlockUserModal: React.FC<BlockUserModalProps> = ({
  open,
  user,
  onClose,
}) => {
  const block = user.userType !== UserType.Blocked;

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const onDeleteSuccess = () => {
    setSnackbar({
      open: true,
      message: "User blocked successfully",
      severity: "success",
    });
    setInProgress(false);
    onClose();
  };

  const onDeleteError = (err: unknown) => {
    console.log(err);
    setSnackbar({
      open: true,
      message: "Failed to block user",
      severity: "error",
    });
    setInProgress(false);
    onClose();
  };

  const { mutate: blockUser } = useBlockUser(onDeleteSuccess, onDeleteError);

  const onUnBlockSuccess = () => {
    setSnackbar({
      open: true,
      message: "User no longer blocked",
      severity: "success",
    });
    setInProgress(false);
    onClose();
  };

  const onUnBlockError = (err: unknown) => {
    console.log(err);
    setSnackbar({
      open: true,
      message: "Failed to unblock user",
      severity: "error",
    });
    setInProgress(false);
    onClose();
  };

  const { mutate: unBlockUser } = useUnBlockUser(
    onUnBlockSuccess,
    onUnBlockError,
  );

  const onConfirm = () => {
    if (!user) {
      return;
    }

    setInProgress(true);

    if (block) {
      blockUser({ userId: user.userId, clientId: user.clientId });
    } else {
      unBlockUser({ userId: user.userId, clientId: user.clientId });
    }
  };

  const onSnackbarClose = () => {
    setSnackbar((current) => ({
      ...current,
      open: false,
    }));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle component="div">
          <Typography variant="h3">
            {block ? "Block User" : "Restore Blocked User"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {!block && (
            <Stack spacing={2}>
              <Typography variant="body1">
                Are you sure you want to unblock user? Once unblocked they can
                be re-added to the site as a guest or user.
              </Typography>
              <Chip label={user.fullName || user.email} />
              <Typography variant="body1">
                <b>Only perform this action if user was incorrectly blocked</b>
              </Typography>
            </Stack>
          )}
          {block && (
            <Stack spacing={2}>
              <Typography variant="body1">
                Do you want to block this user?
              </Typography>
              <Chip label={user.fullName || user.email} />
              <Typography variant="body1">
                A blocked user's email can not be used on clear call for any
                reason.
              </Typography>
              <Typography variant="body1">
                <b>Only developer admins can restore blocked users.</b>
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            disabled={inProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            color="error"
            variant="contained"
            disabled={inProgress}
            fullWidth
          >
            {inProgress ? "In Progress..." : block ? "Block" : "Restore"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={onSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BlockUserModal;
